import {
  Text,
  Button,
  FormControl,
  FormErrorMessage,
  Input,
  Box,
  Grid,
  GridItem,
  Flex,
  FormLabel,
} from "@chakra-ui/react";
import { z } from "@/i18n";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useState } from "react";
import { APISchemas } from "@/api";
import { useTranslation } from "react-i18next";
import { PhoneInput } from "react-international-phone";
import { PhoneNumberUtil } from "google-libphonenumber";
import { EditSquareIconBg } from "@/assets/icons/edit-square-icon-bg";
import { CancelSquareIcon } from "@/assets/icons/cancel-square-icon";
import { CheckSquareIcon } from "@/assets/icons/check-square-icon";

interface EditPersonalInfoProps {
  userInfo: APISchemas["User"];
  handleEdit: (data: ValidationSchemaType) => void;
}
type EditErrorItem = {
  path: string;
  message: Record<string, string>;
};

const useTranslatedValidation = () => {
  const { t } = useTranslation();

  const validationSchema = z.object({
    phone: z.string().max(19, t("invalid_phone")),
    name: z.string().min(1),
    surname: z.string().min(1),
  });

  return validationSchema;
};

type ValidationSchemaType = z.infer<ReturnType<typeof useTranslatedValidation>>;

export const EditPersonalInfo: React.FC<EditPersonalInfoProps> = ({
  handleEdit,
  userInfo,
}) => {
  const [editPersonalInfo, setEditPersonalInfo] = useState<boolean>(false);
  const [editError, setEditError] = useState<any>();
  const [phone, setPhone] = useState<string>(userInfo?.phone ?? "");
  const [isPhoneValid, setIsPhoneValid] = useState<boolean>(true);
  const validationSchema = useTranslatedValidation();
  type validationSchema = z.infer<typeof validationSchema>;

  const { t } = useTranslation();
  const {
    handleSubmit,
    register,
    control,
    reset,
    formState: { errors },
  } = useForm<validationSchema>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      name: userInfo.first_name,
      phone: userInfo.phone ?? undefined,
      surname: userInfo.last_name,
    },
  });
  const phoneUtil = PhoneNumberUtil.getInstance();

  const handleCancel = useCallback(() => {
    reset({
      name: userInfo.first_name,
      phone: userInfo.phone ?? undefined,
      surname: userInfo.last_name,
    });

    setEditPersonalInfo(false);
  }, [reset, userInfo]);

  const validatePhone = useCallback(
    (phone: string) => {
      try {
        return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
      } catch (error) {
        return false;
      }
    },
    [phoneUtil]
  );

  useEffect(() => {
    setIsPhoneValid(validatePhone(phone));
  }, [phone, validatePhone]);
  const onSubmit: SubmitHandler<validationSchema> = useCallback(
    async (data) => {
      if (!isPhoneValid) return;

      const errors = await handleEdit(data);
      setEditError(errors);

      setEditPersonalInfo(false);
    },
    [handleEdit, isPhoneValid]
  );

  useEffect(() => {
    if (editError?.some((error: EditErrorItem) => error !== null)) {
      const timeoutId = setTimeout(() => {
        setEditError(Array(setEditError.length).fill(null));
      }, 10000);
      return () => clearTimeout(timeoutId);
    }
  }, [setEditError, editError]);

  return (
    <Flex w="full">
      <Flex my="3" alignItems="top" w="100%" flex="2">
        <Box whiteSpace="nowrap">
          <Text
            fontWeight="600"
            lineHeight="20px"
            color="black.dark.default"
            fontSize="24px"
          >
            {t("personal_information")}
          </Text>

          <Text color="gray.smoke" fontSize="13px" lineHeight="20px" mt="1.5">
            {t("page.settings.profile.not-shown")}
          </Text>
        </Box>
      </Flex>

      <Box w="full" flex="2.4">
        <form
          style={{
            border: "none",
            display: "flex",
            height: "min-content",
            position: "relative",
          }}
          onSubmit={handleSubmit(onSubmit)}
        >
          <Grid
            w="full"
            templateRows="repeat(1, 1fr)"
            templateColumns="repeat(2, 1fr)"
            columnGap="40px"
          >
            <GridItem colSpan={1}>
              <FormControl
                isInvalid={!!errors.name}
                borderRadius="4px"
                width="full"
                transition="borderColor 0.6s ease"
                h="100px"
              >
                <FormLabel
                  pos="absolute"
                  top="4px"
                  fontSize="16px"
                  zIndex="9"
                  ml="2.5"
                  px="4"
                  fontWeight="400"
                  lineHeight="1"
                  pr="8"
                  bg="white.normal"
                >
                  {t("name")}
                </FormLabel>
                <Input
                  border="1px solid"
                  borderColor={editPersonalInfo ? "blue.shiny" : "gray.passive"}
                  borderRadius="4px"
                  maxLength={20}
                  w="full"
                  mt="3"
                  p="3"
                  color="gray.passive"
                  disabled={!editPersonalInfo}
                  variant="unstyled"
                  _disabled={{
                    color: "gray.passive",
                  }}
                  type="text"
                  _placeholder={{
                    color: "gray.passive",
                  }}
                  {...register("name")}
                  placeholder={userInfo.first_name}
                />

                {editError?.find(
                  (item: EditErrorItem) => item.path === "name"
                ) && (
                  <Text
                    mt="5px"
                    fontSize="14px"
                    fontWeight="500"
                    color="red.main"
                    ml="1"
                  >
                    {
                      editError?.find(
                        (item: EditErrorItem) => item.path === "name"
                      )?.message?.name
                    }
                  </Text>
                )}
                {editPersonalInfo && (
                  <FormErrorMessage>
                    <Text position="absolute" bottom="20px">
                      {errors.name?.message}
                    </Text>
                  </FormErrorMessage>
                )}
              </FormControl>
            </GridItem>

            <GridItem colSpan={1}>
              <FormControl
                borderRadius="4px"
                isInvalid={!!errors.surname}
                width="full"
                h="100px"
              >
                <FormLabel
                  pos="absolute"
                  top="4px"
                  fontSize="16px"
                  zIndex="9"
                  ml="2.5"
                  px="4"
                  fontWeight="400"
                  lineHeight="1"
                  pr="8"
                  bg="white.normal"
                >
                  {t("surname")}
                </FormLabel>

                <Input
                  border="1px solid"
                  borderColor={editPersonalInfo ? "blue.shiny" : "gray.passive"}
                  borderRadius="4px"
                  transition="borderColor 0.6s ease"
                  maxLength={20}
                  variant="unstyled"
                  w="full"
                  mt="3"
                  p="3"
                  disabled={!editPersonalInfo}
                  _disabled={{
                    color: "gray.passive",
                  }}
                  color="gray.passive"
                  type="text"
                  placeholder={userInfo.last_name}
                  _placeholder={{
                    color: "gray.passive",
                  }}
                  {...register("surname")}
                />
                {editError?.find(
                  (item: EditErrorItem) => item.path === "surname"
                ) && (
                  <Text
                    mt="5px"
                    fontSize="14px"
                    fontWeight="500"
                    color="red.main"
                    ml="1"
                  >
                    {
                      editError?.find(
                        (item: EditErrorItem) => item.path === "surname"
                      )?.message?.surname
                    }
                  </Text>
                )}
                {editPersonalInfo && (
                  <FormErrorMessage>
                    <Text position="absolute" bottom="20px">
                      {errors.surname?.message}
                    </Text>
                  </FormErrorMessage>
                )}
              </FormControl>
            </GridItem>

            <GridItem colSpan={1}>
              <FormControl
                mt="3"
                isInvalid={!!errors.phone}
                borderRadius="4px"
                transition="borderColor 0.6s ease"
                w="full"
                h="100px"
                pos="relative"
              >
                <FormLabel
                  pos="absolute"
                  top="-10px"
                  fontSize="16px"
                  zIndex="9"
                  ml="2.5"
                  px="4"
                  fontWeight="400"
                  lineHeight="1"
                  pr="8"
                  bg="white.normal"
                >
                  {t("phone_number")}
                </FormLabel>
                <Box
                  border="1px solid"
                  borderColor={editPersonalInfo ? "blue.shiny" : "gray.passive"}
                  borderRadius="4px"
                  pl="4"
                  py="1.5"
                  sx={{
                    "& .react-international-phone-input": {
                      background: "white.normal",
                      border: "none",
                      width: "full",
                      color: "gray.passive",
                      lineHeight: "1",
                    },
                    "& .react-international-phone-input--disabled": {
                      background: "white.normal",
                      border: "none",
                      width: "full",
                      color: "gray.passive",
                    },
                    "& .react-international-phone-country-selector-button": {
                      background: "white.normal",
                      width: "full",
                      color: "gray.passive",
                      border: "none",
                    },
                  }}
                >
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                      <PhoneInput
                        hideDropdown={true}
                        disabled={!editPersonalInfo}
                        defaultCountry="tr"
                        onChange={(value) => {
                          field.onChange(value);
                          setPhone(value);
                        }}
                        value={field.value}
                      />
                    )}
                  />
                </Box>
                {editError?.find(
                  (item: EditErrorItem) => item.path === "phone"
                ) && (
                  <Text
                    mt="5px"
                    fontSize="14px"
                    fontWeight="500"
                    color="red.main"
                    ml="1"
                  >
                    {
                      editError?.find(
                        (item: EditErrorItem) => item.path === "phone"
                      )?.message?.phone
                    }
                  </Text>
                )}
                {!isPhoneValid && editPersonalInfo && (
                  <Text
                    whiteSpace="nowrap"
                    color="red.main"
                    fontSize="14px"
                    position="relative"
                    top="0"
                  >
                    {t("page.settings.profile.phone-validation")}
                  </Text>
                )}
                {editPersonalInfo && (
                  <FormErrorMessage>
                    <Text position="absolute" bottom="20px">
                      {errors.phone?.message}
                    </Text>
                  </FormErrorMessage>
                )}
              </FormControl>
            </GridItem>

            <GridItem colSpan={1}>
              <FormControl
                borderRadius="4px"
                width="full"
                transition="borderColor 0.6s ease"
                h="100px"
              >
                <FormLabel
                  pos="absolute"
                  top="4px"
                  fontSize="16px"
                  zIndex="9"
                  ml="2.5"
                  px="4"
                  fontWeight="400"
                  lineHeight="1"
                  pr="8"
                  bg="white.normal"
                >
                  {t("email")}
                </FormLabel>
                <Input
                  border="1px solid"
                  borderColor="gray.passive"
                  borderRadius="4px"
                  maxLength={20}
                  w="full"
                  mt="3"
                  p="3"
                  color="gray.passive"
                  variant="unstyled"
                  type="text"
                  disabled={true}
                  _disabled={{
                    color: "gray.passive",
                  }}
                  _placeholder={{
                    color: "gray.passive",
                  }}
                  placeholder={userInfo.email}
                />

                {editError?.find(
                  (item: EditErrorItem) => item.path === "name"
                ) && (
                  <Text
                    mt="5px"
                    fontSize="14px"
                    fontWeight="500"
                    color="red.main"
                    ml="1"
                  >
                    {
                      editError?.find(
                        (item: EditErrorItem) => item.path === "name"
                      )?.message?.name
                    }
                  </Text>
                )}
                {editPersonalInfo && (
                  <FormErrorMessage>
                    <Text position="absolute" bottom="20px">
                      {errors.name?.message}
                    </Text>
                  </FormErrorMessage>
                )}
              </FormControl>
            </GridItem>
          </Grid>
        </form>
      </Box>

      <Flex flex="0.6" justifyContent="right">
        {!editPersonalInfo ? (
          <Button
            cursor="pointer"
            variant="unstyled"
            type="submit"
            onClick={() => {
              setEditPersonalInfo(!editPersonalInfo);
            }}
          >
            <Flex alignItems="center" justifyContent="center">
              <EditSquareIconBg width="40px" height="40px" />
            </Flex>
          </Button>
        ) : (
          <Flex>
            <Button
              border-radius="3px"
              color="white.normal"
              onClick={() => handleCancel()}
              _hover={{
                opacity: "0.6",
              }}
              width="40px"
              height="40px"
            >
              <Flex alignItems="center" justifyContent="center">
                <CancelSquareIcon width="40px" height="40px" />
              </Flex>
            </Button>
            <Button
              border-radius="3px"
              color="white.normal"
              onClick={() => handleSubmit(onSubmit)()}
              _hover={{
                opacity: "0.6",
              }}
              type="submit"
              width="40px"
              height="40px"
              ml="14px"
            >
              <Flex alignItems="center" justifyContent="center">
                <CheckSquareIcon width="40px" height="40px" />
              </Flex>
            </Button>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};
